<template>
  <div class="interactives-push-alert-animation-main">
  </div>
</template>

<script>
import * as d3 from 'd3'

export default {
  name: 'anim-main',
  props: ['alertsByDay', 'currentSimSec', 'userSelectedSimSec', 'lag', 'filter'],
  data () {
    return {
      alertsLoaded: false,
      bisectDate: d3.bisector(d => d.day).right,
      deadline: 1 / 4, // proportion of width by which all alerts must be displayed in column
      justScrubbed: false,
      colWidth: 444,
      tDurs: [150, 400]
    }
  },
  computed: {
    earliestShown () { return d3.timeMinute.offset(this.currentSimSec, -this.minsShown) },
    activeDays () { return d3.timeDay.range(this.earliestShown, this.currentSimSec) }
  },
  watch: {
    alertsByDay: function (newVal) {
      if (newVal.length) {
        this.alertsLoaded = true
        this.renderViz()
      }
    },
    currentSimSec: function (newVal, oldVal) {
      if (this.alertsLoaded) this.setActiveDays(newVal, oldVal)
      if (this.xScale) this.updateScale()
      if (this.column) this.positionColumns()
      this.justScrubbed = newVal === this.userSelectedSimSec
    },
    userSelectedSimSec: function (newVal, oldVal) {
      this.justScrubbed = newVal !== oldVal
    },
    filter: function (newVal) {
      this.updateFilter(newVal)
      this.positionColumns()
    }
  },
  mounted () {
    this.$parent.$on('resize', () => {
      if (this.xScale) {
        this.xScale.range([0, this.vizWidth()])
        this.$emit('update:scaled', this.daysShown)
      }
    })
  },
  methods: {
    vizWidth () {
      const width = this.$el.getBoundingClientRect().width
      this.daysShown = width / this.colWidth
      this.minsShown = this.daysShown * 24 * 60
      return width
    },
    vizHeight () { return this.$el.getBoundingClientRect().height },
    renderViz () {
      this.xScale = d3.scaleTime()
        .range([0, this.vizWidth()])

      this.updateScale()
      this.$emit('update:scaled', this.daysShown)

      this.columns = d3.select(this.$el).append('div')
        .attr('class', 'columns')

      this.dates = d3.select(this.$el).append('div')
        .attr('class', 'ticker')

      this.$on('alertsActive', () => {
        if (!this.alertsActive) this.updateColumns()
        this.alertsActive = true
      })
    },
    setActiveDays (newVal, oldVal) {
      const beginIndex = this.bisectDate(this.alertsByDay, this.earliestShown)
      const endIndex = this.bisectDate(this.alertsByDay, this.currentSimSec) + 1

      this.alertsByDay.forEach((a, i) => {
        const wasActive = a.active
        a.active = i >= beginIndex & i < endIndex

        if (wasActive !== a.active) {
          this.$emit('alertsActive')
          a.change = true
        } else a.change = false
      })

      if (this.alertsByDay.some(a => a.change)) this.updateColumns()
    },
    updateScale (animate) {
      this.xScale.domain([this.earliestShown, this.currentSimSec])
    },
    updateColumns () {
      const activeDays = this.alertsByDay.filter(a => a.active)

      this.date = this.dates.selectAll('.date')
        .data(activeDays, d => d.day)
      this.column = this.columns.selectAll('.column')
        .data(activeDays, d => d.day)

      this.date.exit().remove()
      this.column.exit().remove()

      const newDates = this.date.enter()
        .append('span')
        .attr('class', 'date')
        .style('right', d => `${this.vizWidth() - this.xScale(new Date(d.day))}px`)
        .style('top', 0)
        .text(d => this.formatDate(d.day))

      const newColumns = this.column.enter()
        .append('div')
        .attr('class', 'column')
        .style('right', d => `${this.vizWidth() - this.xScale(new Date(d.day))}px`)
        .style('top', 0)

      const scale = d3.scaleLinear().range([0, this.deadline])

      const alerts = newColumns.append('div')
        .attr('class', 'alerts')
        .style('height', `${this.vizHeight()}px`)

      const drag = d3.drag()
        .on('start', function () { d3.select(this).classed('dragging', true) })
        .on('drag', function () { this.scrollTop = this.scrollTop - d3.event.dy })
        .on('end', function () { d3.select(this).classed('dragging', false) })

      alerts.call(drag)

      const alert = alerts.selectAll('.alert')
        .data(d => d.alerts).enter()
        .insert('div', ':first-child')
        .attr('class', 'alert')
        .classed('alert-highlight', d => d.highlight)
        .classed('trump', d => d.hasTrump)
        .classed('match-alert', d => !d.alertId)

      alerts.append('div')
        .attr('class', 'ghost-alert-bottom')
        .style('height', `${1.5 * d3.select('.gutter').node().getBoundingClientRect().height}px`)

      alerts
        .insert('div', ':first-child')
        .attr('class', 'ghost-alert-top')
        .style('height', `${this.dates.node().getBoundingClientRect().height}px`)

      const header = alert.append('div')
        .attr('class', 'alert-header')
        .append('table')
        .append('tr')

      header.append('td')
        .attr('class', 'times-logo')
        .append('span')
        .attr('class', 'times-image')

      header.append('td')
        .attr('class', 'times-brand')
        .text('NYTIMES')

      const formatter = d3.timeFormat('%-I:%M %p')
      header.append('td')
        .attr('class', 'alert-timestamp')
        .text(d => formatter(d.dateParsed))

      alert.append('div')
        .attr('class', 'alert-body')
        .html(d => d.text)

      alert.style('opacity', '0')
        .style('max-height', '0')
        .call(function (selection) {
          const data = selection.data()
          if (data.length) {
            scale.domain([0, data.length - 1])
          } else scale.domain([0, 0])
        }).each((d, i) => {
          d.checkpoint = 1 - scale(i)
        })

      newDates.merge(this.date)
      newColumns.merge(this.column).call(() => this.$emit('update:vizRendered', true))
    },
    positionColumns () {
      this.date = d3.select(this.$el).selectAll('.date')
        .interrupt()
        .transition()
        .duration(this.lag)
        .ease(d3.easeLinear)
        .style('right', d => `${this.vizWidth() - this.xScale(d.day)}px`)

      this.column = d3.select(this.$el).selectAll('.column')
        .interrupt()
        .transition()
        .duration(this.lag)
        .ease(d3.easeLinear)
        .style('right', d => `${this.vizWidth() - this.xScale(d.day)}px`)

      const filter = this.filter
      const showAlert = this.showAlert
      const vizWidth = this.vizWidth()
      this.column.selectAll('.alert')
        .each(function (d) {
          if (this.parentNode.getBoundingClientRect().right < vizWidth * d.checkpoint) {
            const alert = d3.select(this)
            if (!alert.classed('shown')) {
              if (d.hasTrump && filter !== 'No Trump') alert.call(showAlert)
              else if (!d.hasTrump && filter !== 'Only Trump') alert.call(showAlert)
            }
          }
        })

      this.column.select('.alerts')
        .style('height', `${this.vizHeight()}px`)
        .each(function () {
          if (this.scrollHeight > this.clientHeight) {
            d3.select(this).classed('draggable', true)
          }
        })
    },
    updateFilter (filter) {
      // hide or show Trump alerts
      d3.select(this.$el).selectAll('.alert.trump')
        .call((this.filter === 'All push alerts' | this.filter === 'Only Trump') ? this.showAlert : this.hideAlert)

      // hide or show non-Trump alerts
      d3.select(this.$el).selectAll('.alert:not(.trump)')
        .call((this.filter === 'All push alerts' | this.filter === 'No Trump') ? this.showAlert : this.hideAlert)
    },
    showAlert (alert) {
      if (!alert.classed('shown')) {
        alert
          .interrupt()
          .classed('shown', true)
          .style('display', 'block')
          .style('transform', 'scale3d(0.1,0,1)')
          .transition('grow')
          .duration(this.tDurs[0])
          .style('max-height', '200px')
          .style('margin', '0.7em')
          .style('margin-top', '0')
          .transition('fadeIn')
          .duration(this.tDurs[1])
          .style('transform', 'scale3d(1,1,1)')
          .style('opacity', '1')
      }
    },
    hideAlert (alert) {
      alert
        .interrupt()
        .transition('fadeOut')
        .duration(this.tDurs[1])
        .style('transform', 'scale3d(0.1,0,1)')
        .style('opacity', '0')
        .transition('shrink')
        .duration(this.tDurs[0])
        .style('max-height', '0px')
        .style('padding', '0')
        .style('margin', '0')
        .on('end', function () {
          d3.select(this)
            .style('display', 'none')
            .classed('shown', false)
        })
    },
    formatDate (date) {
      const formatter = d3.timeFormat('%A, %B %-d')
      return formatter(date)
        .replace('January', 'Jan.')
        .replace('February', 'Feb.')
        .replace('August', 'Aug.')
        .replace('September', 'Sept.')
        .replace('October', 'Oct.')
        .replace('November', 'Nov.')
        .replace('December', 'Dec.')
    }
  }
}
</script>

<style lang="scss">
.interactives-push-alert-animation-main {
  width: 100%;
  position: relative;
  overflow: hidden;

  * {
    box-sizing: border-box;
  }

  .alerts {
    overflow: hidden;
  }

  .ghost-alert-bottom, .ghost-alert-top {
    width: 100%;
    opacity: 0;
  }

  .columns {
    position: absolute;
    top: 0;
    right: 0;
  }
}
</style>



// WEBPACK FOOTER //
// Animation.vue?24069301